import { useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { useLazyLogoutUserQuery } from '../api';
import { EAuthRole } from '../types';
import { useAuth } from './useAuth';
import { useRoleGuard } from './useRoleGuard';

/**
 * Enforces that the current app may only be accessed by `allowedRoles`.
 *
 * When an authenticated user's role is not permitted, they are logged out (which
 * also clears the httpOnly refresh cookie, so the refresh flow cannot silently
 * re-authenticate them into the wrong app) and notified once.
 *
 * Returns whether the current user is allowed in this app.
 */
export const useAppGuard = (allowedRoles?: EAuthRole[]) => {
    const { t } = useTranslation('common', { keyPrefix: 'components.loginForm' });
    const { isAuthenticated, user } = useAuth();
    const isRoleAllowed = useRoleGuard(allowedRoles);
    const [logoutUser] = useLazyLogoutUserQuery();
    const handledRef = useRef(false);

    useEffect(() => {
        if (isRoleAllowed) {
            handledRef.current = false;
            return;
        }

        if (isAuthenticated === true && user && !handledRef.current) {
            handledRef.current = true;
            toast.error(t('response.forbiddenRole'));
            logoutUser();
        }
    }, [isAuthenticated, user, isRoleAllowed, logoutUser, t]);

    return isRoleAllowed;
};
